import { DTubePlayer } from "./player";
import { toUUID, encodeFromUUID } from "./base58";
import { getCdnUrls, getVideoUrl, getPosterUrl } from "./cdn";
import "./styles.css";

// Media service URLs
const API_URL = import.meta.env.VITE_API_URL || "https://api.d.tube";
const ADS_URL = import.meta.env.VITE_ADS_URL || "https://a.d.tube";

// CDN selection based on viewer's region
const cdn = getCdnUrls();

interface EmbedParams {
  videoId?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  start?: number;
  nologo?: boolean;
}

interface AdResponse {
  type: "none" | "direct";
  videoUrl?: string;
  duration?: number;
  clickUrl?: string | null;
  skipAfter?: number;
}

function getVideoId(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const pathParts = window.location.pathname.split("/").filter(Boolean);

  let rawId: string | null = null;

  const fromQuery = urlParams.get("v");
  if (fromQuery) {
    rawId = fromQuery;
  } else if (pathParts.length > 0) {
    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart && lastPart !== "embed") {
      rawId = lastPart;
    }
  }

  if (!rawId) return null;

  try {
    return toUUID(rawId);
  } catch {
    return rawId;
  }
}

function getParams(): EmbedParams {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    videoId: getVideoId() || undefined,
    autoplay: urlParams.get("autoplay") === "1",
    muted: urlParams.get("muted") === "1",
    loop: urlParams.get("loop") === "1",
    start: urlParams.has("t") ? parseInt(urlParams.get("t")!, 10) : undefined,
    nologo: urlParams.get("nologo") === "1",
  };
}

function showError(message: string): void {
  const container = document.getElementById("player")!;
  container.innerHTML = `
    <div class="error">
      <div>
        <h1>Video unavailable</h1>
        <p>${message}</p>
      </div>
    </div>
  `;
}

/**
 * The page that hosts this player when it runs inside an iframe, as an
 * origin. ancestorOrigins (Chrome, Safari) names the top-level page no
 * matter what referrer policy it sets; Firefox lacks it, so fall back to
 * document.referrer, which the host's policy may have trimmed or emptied.
 * Opened as a top-level page, this is simply the linking page.
 */
function hostReferrer(framed: boolean): string {
  if (framed) {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      const top = ancestors[ancestors.length - 1];
      if (top && top !== "null") return top;
    }
  }
  return document.referrer;
}

/**
 * Record a view, and if the API says the video doesn't exist in the DB,
 * tear the player down and show an error. Fire-and-forget otherwise —
 * cooldowns, network errors, and other statuses are ignored.
 *
 * The body tells the API whether we're framed (embed vs. direct player) and
 * which page hosts us; the API reduces the referrer to an origin.
 */
function recordView(videoId: string, player: DTubePlayer): void {
  const framed = window.self !== window.top;
  fetch(`${API_URL}/videos/${videoId}/view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ framed, referrer: hostReferrer(framed) }),
  })
    .then((response) => {
      if (response.status === 404) {
        player.dispose().finally(() => {
          showError("This video is no longer available.");
        });
      }
    })
    .catch(() => {});
}

interface SimilarVideo {
  id: string;
  title: string;
  thumbnail_url?: string | null;
  duration?: number;
}

async function fetchSimilarVideos(videoId: string): Promise<SimilarVideo[]> {
  try {
    const res = await fetch(`${API_URL}/videos/${videoId}/similar?limit=6`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.videos) ? data.videos : [];
  } catch {
    return [];
  }
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds || !Number.isFinite(seconds)) return "";
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m}:${s.toString().padStart(2, "0")}`;
}

function thumbnailFor(v: SimilarVideo): string {
  if (v.thumbnail_url) {
    try {
      // Stored URLs carry whichever host the uploader was configured with at
      // encode time; only the path is used, served from the image host.
      const parsed = new URL(v.thumbnail_url);
      if (parsed.hostname === "d.tube" || parsed.hostname.endsWith(".d.tube")) {
        return cdn.images + parsed.pathname + parsed.search;
      }
    } catch {
      // fall through to default thumbnail
    }
  }
  return getPosterUrl(cdn.images, v.id);
}

function watchUrlFor(videoId: string): string {
  try {
    return `https://d.tube/watch/${encodeFromUUID(videoId)}`;
  } catch {
    return `https://d.tube/watch/${videoId}`;
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function hideEndScreen(): void {
  const container = document.getElementById("player");
  container?.querySelector(".dtube-endscreen")?.remove();
}

function showEndScreen(videos: SimilarVideo[]): void {
  if (videos.length === 0) return;
  const container = document.getElementById("player");
  if (!container) return;
  // Don't re-render if already present
  if (container.querySelector(".dtube-endscreen")) return;

  const overlay = document.createElement("div");
  overlay.className = "dtube-endscreen";
  overlay.innerHTML = `
    <div class="dtube-endscreen-grid">
      ${videos
        .map((v) => {
          const duration = formatDuration(v.duration);
          return `
            <a class="dtube-endscreen-card" href="${watchUrlFor(v.id)}" target="_blank" rel="noopener noreferrer">
              <div class="dtube-endscreen-thumb">
                <img src="${thumbnailFor(v)}" alt="" loading="lazy" />
                ${duration ? `<span class="dtube-endscreen-duration">${duration}</span>` : ""}
              </div>
              <div class="dtube-endscreen-title">${escapeHtml(v.title || "")}</div>
            </a>
          `;
        })
        .join("")}
    </div>
  `;
  container.appendChild(overlay);
}

async function fetchAdInfo(videoId: string): Promise<AdResponse> {
  try {
    const response = await fetch(`${ADS_URL}/vast?video_id=${videoId}`);
    if (!response.ok) {
      return { type: "none" };
    }
    return await response.json();
  } catch {
    return { type: "none" };
  }
}

function createAdOverlay(container: HTMLElement, clickUrl: string | null, onSkip: () => void): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "ad-overlay";
  overlay.innerHTML = `
    <div class="ad-label">Ad</div>
    <div class="ad-skip" style="display: none;">
      <button class="ad-skip-button">Skip Ad</button>
    </div>
  `;

  // Handle click on video to open ad URL
  if (clickUrl) {
    overlay.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".ad-skip")) {
        window.open(clickUrl, "_blank", "noopener,noreferrer");
      }
    });
    overlay.style.cursor = "pointer";
  }

  // Handle skip button
  const skipButton = overlay.querySelector(".ad-skip-button");
  skipButton?.addEventListener("click", (e) => {
    e.stopPropagation();
    onSkip();
  });

  container.appendChild(overlay);
  return overlay;
}

function showSkipButton(overlay: HTMLElement): void {
  const skipContainer = overlay.querySelector(".ad-skip") as HTMLElement;
  if (skipContainer) {
    skipContainer.style.display = "block";
  }
}

function removeAdOverlay(overlay: HTMLElement): void {
  overlay.remove();
}

function setupMessageListener(player: DTubePlayer): void {
  window.addEventListener("message", (event) => {
    if (typeof event.data !== "object") return;

    switch (event.data.type) {
      case "dtube:play":
        player.play();
        break;
      case "dtube:pause":
        player.pause();
        break;
      case "dtube:seek":
        if (typeof event.data.time === "number") {
          player.seek(event.data.time);
        }
        break;
      case "dtube:setVolume":
        if (typeof event.data.volume === "number") {
          player.setVolume(event.data.volume);
        }
        break;
      case "dtube:mute":
        player.mute();
        break;
      case "dtube:unmute":
        player.unmute();
        break;
      case "dtube:getP2PStats":
        if (window.parent !== window) {
          window.parent.postMessage({
            type: "dtube:p2pStats",
            stats: player.getP2PStats(),
          }, "*");
        }
        break;
    }
  });
}

async function init(): Promise<void> {
  const params = getParams();

  if (!params.videoId) {
    showError("No video ID specified");
    return;
  }

  const contentSrc = getVideoUrl(cdn.primary, params.videoId);
  const fallbackSrc = getVideoUrl(cdn.fallback, params.videoId);
  const poster = getPosterUrl(cdn.images, params.videoId);

  // Track if view has been counted
  let viewCounted = false;
  // Track if we've already tried the fallback CDN
  let usedFallback = false;

  // Kick off similar-videos fetch in the background so it's ready by the
  // time the video ends. Skipped when looping (ended never stays true) or
  // when the logo is hidden — the nologo embed context is d.tube itself,
  // which already shows its own similar-videos section around the player.
  const similarPromise: Promise<SimilarVideo[]> = params.loop || params.nologo
    ? Promise.resolve([])
    : fetchSimilarVideos(params.videoId);

  // Fetch ad info
  const adInfo = await fetchAdInfo(params.videoId);

  // For direct HLS ads, play ad first then switch to content
  if (adInfo.type === "direct" && adInfo.videoUrl) {
    let adEnded = false;
    let adOverlay: HTMLElement | null = null;
    const skipAfterSeconds = adInfo.skipAfter ?? 5;

    const player = new DTubePlayer("#player", {
      src: adInfo.videoUrl, // Start with ad video
      poster,
      videoId: params.videoId,
      autoplay: params.autoplay,
      muted: params.muted,
      loop: false, // Don't loop ads
      showLogo: !params.nologo,
      onReady: () => {
        if (window.parent !== window) {
          window.parent.postMessage({ type: "dtube:ready" }, "*");
        }
      },
      onPlay: () => {
        // Create ad overlay on first play
        if (!adOverlay && !adEnded) {
          const container = document.getElementById("player")!;
          adOverlay = createAdOverlay(container, adInfo.clickUrl || null, () => {
            // Skip ad
            if (adOverlay) removeAdOverlay(adOverlay);
            adEnded = true;
            switchToContent();
          });
        }
      },
      onTimeUpdate: (currentTime) => {
        // Show skip button after X seconds
        if (currentTime >= skipAfterSeconds && adOverlay) {
          showSkipButton(adOverlay);
        }
      },
      onEnded: () => {
        // Ad finished, switch to content
        if (!adEnded) {
          adEnded = true;
          if (adOverlay) removeAdOverlay(adOverlay);
          switchToContent();
        }
      },
      onError: () => {
        // Ad failed, just play content
        console.warn("Ad failed to load, playing content");
        if (adOverlay) removeAdOverlay(adOverlay);
        adEnded = true;
        switchToContent();
      },
    });

    async function switchToContent(): Promise<void> {
      // Switch to content video, try fallback CDN if primary fails
      try {
        await player.setSource(contentSrc);
      } catch {
        if (!usedFallback) {
          usedFallback = true;
          console.warn("Primary CDN failed for content, trying fallback");
          await player.setSource(fallbackSrc);
        }
      }
      player.play();

      // Apply start time if specified
      if (params.start && params.start > 0) {
        // Wait a bit for video to load before seeking
        setTimeout(() => {
          player.seek(params.start!);
        }, 100);
      }

      // Setup content play handler for view counting
      const video = player.getVideoElement();
      if (video) {
        const handleContentPlay = () => {
          hideEndScreen();
          if (!viewCounted) {
            viewCounted = true;
            recordView(params.videoId!, player);
          }
          if (window.parent !== window) {
            window.parent.postMessage({ type: "dtube:play" }, "*");
          }
        };

        const handleContentEnded = () => {
          similarPromise.then(showEndScreen);
          if (window.parent !== window) {
            window.parent.postMessage({ type: "dtube:ended" }, "*");
          }
        };

        video.addEventListener("play", handleContentPlay);
        video.addEventListener("ended", handleContentEnded);
      }
    }

    setupMessageListener(player);
    return;
  }

  // No ad or unsupported ad type - just play content directly
  const player = new DTubePlayer("#player", {
    src: contentSrc,
    poster,
    videoId: params.videoId,
    autoplay: params.autoplay,
    muted: params.muted,
    loop: params.loop,
    showLogo: !params.nologo,
    onReady: () => {
      // Seek to start time if specified
      if (params.start && params.start > 0) {
        player.seek(params.start);
      }

      // Notify parent window
      if (window.parent !== window) {
        window.parent.postMessage({ type: "dtube:ready" }, "*");
      }
    },
    onPlay: () => {
      hideEndScreen();
      // Count view on first play
      if (!viewCounted) {
        viewCounted = true;
        recordView(params.videoId!, player);
      }

      if (window.parent !== window) {
        window.parent.postMessage({ type: "dtube:play" }, "*");
      }
    },
    onPause: () => {
      if (window.parent !== window) {
        window.parent.postMessage({ type: "dtube:pause" }, "*");
      }
    },
    onEnded: () => {
      similarPromise.then(showEndScreen);
      if (window.parent !== window) {
        window.parent.postMessage({ type: "dtube:ended" }, "*");
      }
    },
    onError: async (error) => {
      // Try fallback CDN before showing error, resuming where playback died
      if (!usedFallback) {
        usedFallback = true;
        console.warn("Primary CDN failed, trying fallback:", error.message);
        try {
          const resumeAt = player.getCurrentTime() || undefined;
          await player.setSource(fallbackSrc, resumeAt);
          return;
        } catch {
          // Fallback also failed
        }
      }
      showError(error.message || "Failed to load video");
    },
  });

  setupMessageListener(player);
}

init();
